import { useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/useSession";
import { usePortalUser } from "@/hooks/usePortalUser";

function hasTasksGrant(grants: { capability: string }[] | undefined): boolean {
  if (!grants?.length) return false;
  return grants.some((g) => g.capability === "tasks" || g.capability === "site_admin" || g.capability === "manage_users");
}

export function TasksProtected({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, loading } = useSession();
  const { portalSession, checking } = usePortalUser(session);

  const next = useMemo(() => {
    const q = `${location.pathname}${location.search}${location.hash}`;
    return q.startsWith("/") ? q : "/tasks";
  }, [location.hash, location.pathname, location.search]);

  const hasAccess = hasTasksGrant(portalSession?.grants);

  useEffect(() => {
    if (loading) return;
    if (!session) {
      navigate(`/tasks/login?next=${encodeURIComponent(next)}`, { replace: true });
    }
  }, [loading, session, navigate, next]);

  if (loading || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-gray text-brand-blue">
        Loading…
      </div>
    );
  }

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-gray text-brand-blue">
        Verifying access…
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-brand-blue px-4 text-center text-brand-gray">
        <p className="font-display text-2xl font-bold">Access not authorised</p>
        <p className="mt-4 max-w-md text-sm text-brand-gray/90">
          {session.user.email} is not registered for Tasks access. Ask a portal manager to add you in{" "}
          <span className="font-semibold">Admin → Users</span> by selecting <span className="font-semibold">Tasks access</span>.
        </p>
        <button
          type="button"
          onClick={() => void supabase.auth.signOut()}
          className="mt-8 rounded-full bg-brand-maroon px-8 py-3 text-sm font-bold uppercase tracking-wide text-white"
        >
          Sign out
        </button>
        <Link to="/" className="mt-6 text-sm underline">
          Back to site
        </Link>
      </div>
    );
  }

  return <>{children}</>;
}

